import {
  Box,
  Button,
  HStack,
  Image,
  Spacer,
  Tbody,
  Td,
  Text,
  Tr,
} from "@chakra-ui/react";

import { Table } from "@chakra-ui/react";
import { format } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { fetchRoyaltyData } from "../../api/pdfExport";
import ReportCalendarComponent from "./reportComponents/reportDatePicker";
import RoyaltyFeeTable from "./reportComponents/royaltyFeeTable";

const formatDate = (date) => {
  return date.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const RoyaltyReport = ({
  customer,
  hideButtons = false,
  externalStartDate,
  externalEndDate,
  externalPrintRef,
}) => {
  const [sectionsData, setSectionsData] = useState({});

  const [triggerRefesh, setTriggerRefresh] = useState(true);

  const now = new Date();

  const year = now.getUTCFullYear();
  const currentMonth = now.getUTCMonth();
  const previousMonth = currentMonth === 0 ? 11 : currentMonth - 1;
  const previousMonthYear = currentMonth === 0 ? year - 1 : year;

  const firstDay = new Date(Date.UTC(previousMonthYear, previousMonth, 1));

  const lastDay = new Date(Date.UTC(year, currentMonth, 0));

  const [startDate, setStartDate] = useState(firstDay);
  const [endDate, setEndDate] = useState(lastDay);

  const printRef = useRef(null);

  const handlePrint = useReactToPrint({
    contentRef: externalPrintRef ? externalPrintRef : printRef,
    pageStyle: `
      @page {
        size: A4 portrait;
        margin: 20px 60px;  /* Adjust margin */
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
        }
      }
    `,
    documentTitle: `Franchise Statement ${formatDate(
      externalStartDate ? externalStartDate : startDate
    )}-${formatDate(externalEndDate ? externalEndDate : endDate)}`,
  });

  useEffect(() => {
    const controller = new AbortController();

    const fetchAllRoyalty = async () => {
      const formattedStartDate = format(
        externalStartDate || startDate,
        "yyyy-MM-dd"
      );
      const formattedEndDate = format(externalEndDate || endDate, "yyyy-MM-dd");

      try {
        const response = await fetchRoyaltyData(
          customer.id,
          formattedStartDate,
          formattedEndDate,
          controller.signal
        );
        setSectionsData(response);
      } catch (err) {
        console.error(err);
      }
    };

    fetchAllRoyalty();

    return () => {
      controller.abort();
    };
  }, [
    customer.id,
    startDate,
    endDate,
    externalStartDate,
    externalEndDate,
    triggerRefesh,
  ]);

  return (
    <Box width={"auto"} overflowX={"auto"} overflowY={"auto"}>
      {!hideButtons && (
        <HStack mb={"20px"}>
          <Button onClick={handlePrint}>Print / Save as PDF</Button>
          <Spacer />
          <ReportCalendarComponent
            callbackFunctionDate={setStartDate}
            date={startDate}
            triggerSetter={setTriggerRefresh}
          ></ReportCalendarComponent>
          <Text color={"white"}>TO</Text>
          <ReportCalendarComponent
            callbackFunctionDate={setEndDate}
            date={endDate}
            triggerSetter={setTriggerRefresh}
          ></ReportCalendarComponent>
        </HStack>
      )}
      <Box ref={externalPrintRef ? externalPrintRef : printRef}>
        <Box
          w="794px"
          bg="white"
          p="40px 60px"
          position="relative"
          css={{
            "@media print": {
              padding: "0 20px !important",
            },
          }}
        >
          <HStack justify="space-between" align="center" mb="20px">
            <Box>
              <Text fontSize="2xl" fontWeight="bold">
                Franchise Statement
              </Text>
              <Text fontSize="sm" color="gray.500">
                {customer.name}
              </Text>
              <Text fontSize="sm" color="gray.500">
                Reporting period from{" "}
                {formatDate(externalStartDate ? externalStartDate : startDate)}{" "}
                - {formatDate(externalEndDate ? externalEndDate : endDate)}
              </Text>
            </Box>
            <Image
              height="60px"
              width={"260px"}
              objectFit="contain"
              src={
                customer?.logo
                  ? customer.logo
                  : "../lucro-logo-light.png"
              }
              alt="MoreLucro Logo"
            />
          </HStack>

          <Box overflowX="auto" mb={"2px"}>
            <Table variant="unstyled">
              <Tbody>
                <Tr borderY="2px solid grey">
                  <Td></Td>
                  <Td
                    fontSize="14px"
                    textAlign="right"
                    fontWeight="bold"
                    p={"2px 30px"}
                  >
                    Total
                  </Td>
                </Tr>
              </Tbody>
            </Table>
          </Box>

          <RoyaltyFeeTable
            fees={sectionsData?.fees}
            total={sectionsData?.totals?.overall}
          />
          <Box
            overflowX="auto"
            mt={"2px"}
            borderBottom={"2px solid grey"}
          ></Box>
        </Box>
      </Box>
    </Box>
  );
};

export default RoyaltyReport;
